import { useState, useEffect } from 'react';
import { DateTime } from 'luxon';

import {
  STRINGS_REGEX,
  ANY_FIELD_IDENTIFIER_REGEX,
} from 'apps/web_forms/expressions/regularExpressions';
import { Parser } from 'apps/web_forms/expressions/jisonParser';

import {
  getValueFromResourceLookup,
  getFieldFromSource,
} from 'apps/web_forms/expressions/utils';
import {
  getGroupCount,
  getInputMetadata,
  getIndexOf,
} from 'apps/web_forms/expressions/expressionReduxFunctions';

import { locationParse } from 'apps/web_forms/utils/locationHelpers';
import { objectHasProperty } from 'apps/web_forms/utils/resourcesUtils';
import { LATEST } from 'apps/web_forms/utils/constants';
import { useCallback } from 'react';

const useParser = () => {
  const localParser = new Parser();

  // Shared scope with jison parser.
  Parser.prototype.sharedScope = {
    lookup: getValueFromResourceLookup,
    getFieldFromSource,
    DateTime,
    getGroupCount,
    locationParse,
    getInputMetadata,
    getIndexOf,
    currentFieldIdentifier: undefined,
    getResourceVersionNeeded: undefined,
  };

  const parseFormula = useCallback((formula, fieldIdentifier, resourceVersions) => {
    try {
      Parser.prototype.sharedScope.currentFieldIdentifier = fieldIdentifier;
      Parser.prototype.sharedScope.getResourceVersionNeeded = constructGetResourceVersionNeeded(resourceVersions);
      return localParser.parse(formula);
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [localParser]);

  return parseFormula;
};

/**
 * Returns an array with all field identifiers found in the given expression
 * @param {string} expr
 * @returns Array
 */
export const getExpressionFields = (expr) => {
  let matches = expr
    .replace(STRINGS_REGEX, '')
    .matchAll(ANY_FIELD_IDENTIFIER_REGEX);

  if (expr.match(/\[([0]|[1-9]*)\]\//)) {
    let matchesArray = [];
    matchesArray.push(
      [...matches].map((groups) => groups[groups.length - 1]).join('/')
    );
    return matchesArray;
  } else {
    return [...matches].map((groups) => groups[groups.length - 1]) || [];
  }
};

/**
 * Check if a provided expression fulfills a certain pattern
 * @param {String} expression
 * @param {String} pattern
 */
export const expressionFulfillsPatternTemplate = (expression, pattern) => {
  let fulfillsTemplate = false;
  try {
    let fieldExpressions = getExpressionFields(expression);
    fieldExpressions = [...new Set(fieldExpressions)];
    fieldExpressions &&
      fieldExpressions.forEach((field) => {
        expression = expression.replaceAll(field, '::FIELD::');
      });
    fulfillsTemplate = expression.startsWith(pattern);
  } catch (e) {
    console.error(e);
    fulfillsTemplate = false;
  } finally {
    return fulfillsTemplate;
  }
};

/**
 * Check if string is a number
 * @param {String} str
 */
export const isNumber = (str) => {
  const numericStringRegex = new RegExp(`^-?[0-9]+(\.[0-9]+)?$`);
  if (!numericStringRegex.exec(str)) {
    return false;
  }
  return numericStringRegex.exec(str)[0] !== '';
};

/**
 * If string is wrapped with double quotes, remove them
 * @param {string} str
 */
const stripWrappingQuotes = (str) => str.replace(/^"(.*)"$/, '$1');

/**
 * Construct a callback function that gets the version number needed for a given resource
 * @param {Object.<string, number>} resourceVersions
 */
const constructGetResourceVersionNeeded = (resourceVersions) => {
  const getResourceVersionNeeded = (resourceIdHash) => {
    if (!resourceVersions) {
      return LATEST;
    }
    const strippedResourceIdHash = stripWrappingQuotes(resourceIdHash);
    const isSpecificVersionRequired =
      objectHasProperty(resourceVersions, strippedResourceIdHash) &&
      resourceVersions[strippedResourceIdHash] != null;
    const versionNeeded = isSpecificVersionRequired
      ? resourceVersions[strippedResourceIdHash]
      : LATEST;
    return versionNeeded;
  };

  return getResourceVersionNeeded;
};

export default useParser;
